import { motion } from 'framer-motion';
import { Check, Plus, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useState } from 'react';

const BotCard = ({
  bot,
  isSelected = false,
  onSelect,
  onDeselect,
  disabled = false,
  showDescription = true,
  compact = false
}) => {
  const { t } = useTranslation(['bots', 'gameCreation']);
  const [imageError, setImageError] = useState(false);

  if (!bot) return null;

  const handleToggle = () => {
    if (disabled) return;

    if (isSelected) {
      onDeselect?.(bot.id);
    } else {
      onSelect?.(bot.id);
    }
  };

  const getDifficultyColor = (difficulty) => {
    switch (difficulty) {
      case 'easy': return 'bg-green-600';
      case 'medium': return 'bg-yellow-600';
      case 'hard': return 'bg-red-600';
      case 'expert': return 'bg-purple-600';
      default: return 'bg-gray-600';
    }
  };

  const getTraitColors = (trait) => {
    // Every trait used by a roster in server/bots/ gets an explicit color;
    // the original 12 assignments are unchanged. One shared map = the same
    // trait shows the same color in every game's picker.
    const colorMap = {
      leader: 'bg-blue-500',
      wise: 'bg-purple-500',
      decisive: 'bg-red-500',
      supportive: 'bg-green-500',
      eloquent: 'bg-indigo-500',
      diplomatic: 'bg-teal-500',
      brave: 'bg-orange-500',
      faithful: 'bg-yellow-500',
      strategic: 'bg-gray-500',
      compassionate: 'bg-pink-500',
      protective: 'bg-emerald-500',
      insightful: 'bg-violet-500',
      bold: 'bg-red-600',
      courageous: 'bg-orange-600',
      cunning: 'bg-lime-600',
      devoted: 'bg-rose-500',
      discerning: 'bg-cyan-600',
      disciplined: 'bg-slate-500',
      greedy: 'bg-amber-600',
      impulsive: 'bg-fuchsia-500',
      musical: 'bg-sky-500',
      patient: 'bg-teal-600',
      reckless: 'bg-rose-600',
      reluctant: 'bg-zinc-500',
      stubborn: 'bg-stone-500',
      wandering: 'bg-cyan-500',
      steadfast: 'bg-blue-600',
      prudent: 'bg-emerald-600',
      resolute: 'bg-indigo-600',
      loyal: 'bg-green-600',
      persevering: 'bg-purple-600',
      wholehearted: 'bg-pink-600',
      optimistic: 'bg-yellow-600',
      earnest: 'bg-sky-600',
      visionary: 'bg-violet-600',
      prayerful: 'bg-fuchsia-600',
      hopeful: 'bg-lime-500',
      devout: 'bg-amber-500',
      doubting: 'bg-slate-600',
      questioning: 'bg-cyan-700',
      honest: 'bg-teal-700'
    };
    if (colorMap[trait]) return colorMap[trait];
    // Unmapped (future) traits hash into a fixed palette instead of going
    // gray — deterministic, so the same trait always lands on the same
    // color. Complete class strings, or Tailwind's build would purge them.
    const palette = [
      'bg-blue-500', 'bg-purple-500', 'bg-red-500', 'bg-green-500',
      'bg-indigo-500', 'bg-teal-500', 'bg-orange-500', 'bg-yellow-500',
      'bg-pink-500', 'bg-emerald-500', 'bg-violet-500', 'bg-cyan-600',
      'bg-rose-500', 'bg-amber-600', 'bg-sky-500', 'bg-lime-600'
    ];
    let hash = 0;
    for (let i = 0; i < trait.length; i++) {
      hash = ((hash * 31) + trait.charCodeAt(i)) >>> 0;
    }
    return palette[hash % palette.length];
  };

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.9 }}
      animate={{ opacity: 1, scale: 1 }}
      whileHover={{ scale: disabled ? 1 : 1.02 }}
      whileTap={{ scale: disabled ? 1 : 0.98 }}
      className={`
        relative rounded-xl border-2 transition-all duration-200 cursor-pointer
        ${compact ? 'p-3' : 'p-4'}
        ${isSelected
          ? 'border-blue-500 bg-blue-500/10 shadow-lg shadow-blue-500/20'
          : 'border-gray-600 bg-gray-800/50 hover:border-gray-500'
        }
        ${disabled ? 'opacity-50 cursor-not-allowed' : ''}
      `}
      onClick={handleToggle}
    >
      {/* Selection Indicator */}
      <div className="absolute top-2 right-2 z-10">
        {isSelected ? (
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            className="w-6 h-6 bg-blue-500 rounded-full flex items-center justify-center"
          >
            <Check className="w-4 h-4 text-white" />
          </motion.div>
        ) : (
          <motion.div
            whileHover={{ scale: 1.1 }}
            className="w-6 h-6 bg-gray-600 hover:bg-gray-500 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
          >
            <Plus className="w-4 h-4 text-white" />
          </motion.div>
        )}
      </div>

      <div className={`flex ${compact ? 'gap-3' : 'gap-4'}`}>
        {/* Bot Image */}
        <div className={`flex-shrink-0 ${compact ? 'w-12 h-12' : 'w-16 h-16'}`}>
          {!imageError ? (
            <img
              src={bot.image}
              alt={bot.name}
              className="w-full h-full rounded-lg object-cover border border-gray-600"
              onError={() => setImageError(true)}
            />
          ) : (
            <div className="w-full h-full rounded-lg bg-gray-700 border border-gray-600 flex items-center justify-center">
              <span className="text-gray-400 text-xs font-bold">
                {bot.name.charAt(0)}
              </span>
            </div>
          )}
        </div>

        {/* Bot Info */}
        <div className="flex-1 min-w-0">
          {/* Name and Difficulty */}
          <div className="flex items-center justify-between mb-2">
            <h3 className={`font-semibold text-white truncate ${compact ? 'text-sm' : 'text-base'}`}>
              {bot.name}
            </h3>
            <span
              className={`
                px-2 py-1 rounded-full text-xs font-medium text-white flex-shrink-0 ml-2
                ${getDifficultyColor(bot.difficulty)}
              `}
            >
              {t(`selection.difficulty.${bot.difficulty}`, { ns: 'bots' })}
            </span>
          </div>

          {/* Description */}
          {showDescription && !compact && bot.description && (
            <p className="text-gray-400 text-sm mb-3 line-clamp-2">
              {bot.description}
            </p>
          )}

          {/* Personality Traits */}
          <div className="flex flex-wrap gap-1">
            {bot.personality?.slice(0, compact ? 2 : 3).map((trait) => (
              <span
                key={trait}
                className={`
                  px-2 py-1 rounded-full text-xs font-medium text-white
                  ${getTraitColors(trait)}
                `}
              >
                {t(`traits.${trait}`, { ns: 'bots' })}
              </span>
            ))}
            {bot.personality?.length > (compact ? 2 : 3) && (
              <span className="px-2 py-1 rounded-full text-xs font-medium text-gray-400 bg-gray-700">
                +{bot.personality.length - (compact ? 2 : 3)}
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Action Button */}
      {(!compact || isSelected) && (
        <div className={`mt-3 flex items-center ${compact ? 'justify-end' : 'justify-between'}`}>
          {!compact && (
            <span className="text-xs text-gray-500">
              {t('selection.info.level', { ns: 'bots' })}: {t(`selection.difficulty.${bot.difficulty}`, { ns: 'bots' })}
            </span>
          )}

          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={(e) => {
              e.stopPropagation();
              handleToggle();
            }}
            disabled={disabled}
            className={`
              px-3 py-1 rounded-lg text-xs font-medium transition-colors
              ${isSelected
                ? 'bg-red-600 hover:bg-red-700 text-white'
                : 'bg-blue-600 hover:bg-blue-700 text-white'
              }
              ${disabled ? 'opacity-50 cursor-not-allowed' : ''}
            `}
          >
            {isSelected ? (
              <>
                <X className="w-3 h-3 inline mr-1" />
                {t('selection.actions.remove', { ns: 'bots' })}
              </>
            ) : (
              <>
                <Plus className="w-3 h-3 inline mr-1" />
                {t('selection.actions.add', { ns: 'bots' })}
              </>
            )}
          </motion.button>
        </div>
      )}
    </motion.div>
  );
};

export default BotCard;